import { useState } from "react";
import { useGuide } from "@/hooks/useGuide";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { motion } from "framer-motion";
import { X, ArrowRight, Lightbulb, ChevronUp, ChevronDown } from "lucide-react";

const FloatingGuide = () => {
  const { user } = useAuth();
  const {
    currentTip, dismissTip, showGuide, toggleGuide,
    totalTipsForPage, completedTipsForPage,
  } = useGuide();
  const navigate = useNavigate();
  const location = useLocation();
  const [minimized, setMinimized] = useState(false);

  if (!user) return null;
  // Mute assistant during chat — no interruptions while learning
  if (location.pathname === "/chat") return null;

  const progress = totalTipsForPage > 0
    ? Math.round((completedTipsForPage / totalTipsForPage) * 100)
    : 0;

  return (
    <>
      <motion.button
        onClick={toggleGuide}
        className="fixed bottom-20 right-4 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-110"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileTap={{ scale: 0.9 }}
        title="Assistant KelasAI"
      >
        <Lightbulb className="h-5 w-5" />
        {currentTip && (
          <span className="absolute -right-1 -top-1 h-4 w-4 rounded-full bg-destructive animate-pulse" />
        )}
        {totalTipsForPage > 0 && (
          <svg className="absolute inset-0 h-12 w-12 -rotate-90" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="22" fill="none" stroke="hsl(var(--primary-foreground))" strokeWidth="2" opacity="0.2" />
            <circle
              cx="24"
              cy="24"
              r="22"
              fill="none"
              stroke="hsl(var(--primary-foreground))"
              strokeWidth="2.5"
              strokeDasharray={`${progress * 1.38} 999`}
              strokeLinecap="round"
            />
          </svg>
        )}
      </motion.button>

      {showGuide && currentTip && !minimized ? (
        <motion.div
          key={currentTip.id}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed bottom-[8.5rem] right-4 z-[60] w-[300px] max-w-[calc(100vw-2rem)]"
        >
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b border-border bg-primary/5 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">{currentTip.icon || "💡"}</span>
                <h4 className="text-sm font-black text-foreground">{currentTip.title}</h4>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setMinimized(true)}
                  className="rounded-lg p-1 transition-colors hover:bg-muted"
                >
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
                <button
                  onClick={dismissTip}
                  className="rounded-lg p-1 transition-colors hover:bg-muted"
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>

            <div className="px-4 py-3">
              <p className="text-sm font-medium leading-relaxed text-muted-foreground">
                {currentTip.message}
              </p>
            </div>

            {currentTip.action && (
              <div className="px-4 pb-3">
                <button
                  onClick={() => {
                    dismissTip();
                    navigate(currentTip.action!.path);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  {currentTip.action.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <div className="flex items-center justify-between px-4 pb-3">
              {totalTipsForPage > 0 && (
                <span className="text-[10px] font-bold text-muted-foreground">
                  {completedTipsForPage}/{totalTipsForPage}
                </span>
              )}
              <button
                onClick={dismissTip}
                className="ml-auto text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                Compris ✓
              </button>
            </div>
          </div>
        </motion.div>
      ) : null}

      {showGuide && currentTip && minimized ? (
        <motion.button
          key={`${currentTip.id}-minimized`}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setMinimized(false)}
          className="fixed bottom-[8.5rem] right-4 z-[60] flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 shadow-lg transition-colors hover:bg-muted"
        >
          <span className="text-base">{currentTip.icon || "💡"}</span>
          <span className="max-w-[180px] truncate text-xs font-bold text-foreground">{currentTip.title}</span>
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
        </motion.button>
      ) : null}
    </>
  );
};

export default FloatingGuide;
